import React from "react";
import Home from "../Home/Home";
import { connect } from "react-redux";
import {
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Chip,
  Tooltip,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import HelpIcon from "@material-ui/icons/Help";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";

import {
  getAgent,
  updateAgent,
  addAgentExample,
  testAgent,
  clearAgentTest,
} from "../../Redux/Actions/agent";
import { selectAgent, selectAgentTestResult } from "../../Redux/Selectors";
import "./style.css";

interface IProps {
  agent: any;
  testResult: any;
  loading: boolean;
  saving: boolean;
  testing: boolean;
  getAgent: () => void;
  updateAgent: (payload: any) => void;
  addAgentExample: (payload: any) => void;
  testAgent: (payload: any) => void;
  clearAgentTest: () => void;
}

const TONES = [
  { value: "friendly", label: "Friendly" },
  { value: "professional", label: "Professional" },
  { value: "direct", label: "Direct" },
  { value: "consultative", label: "Consultative" },
];

const emptyResource = () => ({
  id: `resource-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
  name: "",
  url: "",
  note: "",
  enabled: true,
});

class AgentSettings extends React.Component<IProps> {
  state = {
    form: null as any,
    newExample: { situation: "", preferredResponse: "" },
    testMessage: "",
  };

  componentDidMount() {
    this.props.getAgent();
  }

  static getDerivedStateFromProps(nextProps: IProps, prevState: any) {
    if (nextProps.agent && !prevState.form) {
      return { form: { ...nextProps.agent } };
    }
    return null;
  }

  setField = (field: string, value: any) => {
    this.setState((prev: any) => ({ form: { ...prev.form, [field]: value } }));
  };

  save = () => {
    const { form } = this.state;
    if (!form) return;
    this.props.updateAgent({
      name: form.name,
      enabled: form.enabled,
      companyName: form.companyName,
      companyDetails: form.companyDetails,
      offerDetails: form.offerDetails,
      goal: form.goal,
      tone: form.tone,
      instructions: form.instructions,
      signature: form.signature,
      signatureIncluded: form.signatureIncluded,
      maxWords: Number(form.maxWords),
      maxParagraphs: Number(form.maxParagraphs),
      forbiddenTopics: form.forbiddenTopics,
      guardrails: form.guardrails,
      resourceLinks: form.resourceLinks,
      approvedExamples: form.approvedExamples,
    });
  };

  updateResource = (index: number, field: string, value: any) => {
    const resources = [...(this.state.form.resourceLinks || [])];
    resources[index] = { ...resources[index], [field]: value };
    this.setField("resourceLinks", resources);
  };

  addResource = () => {
    const resources = [...(this.state.form.resourceLinks || []), emptyResource()];
    this.setField("resourceLinks", resources);
  };

  removeResource = (index: number) => {
    const resources = (this.state.form.resourceLinks || []).filter(
      (_: any, i: number) => i !== index
    );
    this.setField("resourceLinks", resources);
  };

  addExample = () => {
    const { newExample } = this.state;
    if (!newExample.situation.trim() || !newExample.preferredResponse.trim()) return;
    this.props.addAgentExample(newExample);
    this.setState({ newExample: { situation: "", preferredResponse: "" } });
  };

  removeExample = (index: number) => {
    const examples = (this.state.form.approvedExamples || []).filter(
      (_: any, i: number) => i !== index
    );
    this.setField("approvedExamples", examples);
  };

  runTest = () => {
    if (!this.state.testMessage.trim()) return;
    this.props.testAgent({
      subject: "Test message",
      body: this.state.testMessage,
    });
  };

  render() {
    const { form, newExample, testMessage } = this.state;
    const { loading, saving, testing, testResult } = this.props;

    if (loading && !form) {
      return (
        <Home>
          <div className="agentLoading">
            <CircularProgress />
          </div>
        </Home>
      );
    }
    if (!form) return null;

    const readiness = form.readiness || {};

    return (
      <Home>
        <div className="agentWrapper">
          <div className="agentHeader">
            <div>
              <span className="heading">AI Reply Agent </span>
              <span className="headingTooltip">
                <Tooltip
                  title="The agent reads new email replies and drafts an answer using the details below. You always review before anything sends."
                  placement="right-start"
                  arrow
                >
                  <HelpIcon />
                </Tooltip>
              </span>
              <div className="agentSub">
                Version {form.version} · {readiness.exampleCount || 0} examples ·{" "}
                {readiness.resourceCount || 0} resources
              </div>
            </div>
            <div className="agentHeaderActions">
              <Button
                variant="contained"
                className="agentPrimaryButton"
                onClick={this.save}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save agent"}
              </Button>
              <Button
                variant="outlined"
                color={form.enabled ? "secondary" : "primary"}
                onClick={() => this.setField("enabled", !form.enabled)}
              >
                {form.enabled ? "Disable agent" : "Enable agent"}
              </Button>
            </div>
          </div>

          <div className="agentGrid">
            <div className="agentCard">
              <div className="agentCardTitle">Company facts</div>
              <div className="agentCardHint">
                These are the only facts the agent may use. Anything not written here will not be
                invented.
              </div>
              <TextField
                label="Agent name"
                value={form.name || ""}
                onChange={(event) => this.setField("name", event.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                className="agentField"
              />
              <TextField
                label="Company name"
                value={form.companyName || ""}
                onChange={(event) => this.setField("companyName", event.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                className="agentField"
              />
              <TextField
                label="Company details"
                value={form.companyDetails || ""}
                onChange={(event) => this.setField("companyDetails", event.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rows={3}
                className="agentField"
                placeholder="What the company does, who it serves, differentiators"
              />
              <TextField
                label="Offer details"
                value={form.offerDetails || ""}
                onChange={(event) => this.setField("offerDetails", event.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rows={3}
                className="agentField"
                placeholder="Plans, pricing, packages, guarantees"
              />
              <TextField
                label="Goal of a reply"
                value={form.goal || ""}
                onChange={(event) => this.setField("goal", event.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                className="agentField"
                placeholder="e.g. book a 15 minute call"
              />
            </div>

            <div className="agentCard">
              <div className="agentCardTitle">How the agent should reply</div>
              <div className="agentCardHint">
                Guidance may refine voice only. It can never override facts or the safety rules.
              </div>
              <TextField
                select
                label="Tone"
                value={form.tone || "friendly"}
                onChange={(event) => this.setField("tone", event.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                className="agentField"
              >
                {TONES.map((tone) => (
                  <MenuItem key={tone.value} value={tone.value}>
                    {tone.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Instructions"
                value={form.instructions || ""}
                onChange={(event) => this.setField("instructions", event.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rows={4}
                className="agentField"
                placeholder="e.g. Always mention the free pilot. Never promise discounts."
              />
              <TextField
                label="Guardrails"
                value={form.guardrails || ""}
                onChange={(event) => this.setField("guardrails", event.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rows={2}
                className="agentField"
                placeholder="Extra limits, compliance notes"
              />
              <TextField
                label="Never discuss (comma separated)"
                value={(form.forbiddenTopics || []).join(", ")}
                onChange={(event) =>
                  this.setField(
                    "forbiddenTopics",
                    event.target.value
                      .split(",")
                      .map((entry) => entry.trim())
                      .filter(Boolean)
                  )
                }
                variant="outlined"
                size="small"
                fullWidth
                className="agentField"
              />
              <div className="agentRow">
                <TextField
                  label="Max words"
                  type="number"
                  value={form.maxWords || 180}
                  onChange={(event) => this.setField("maxWords", event.target.value)}
                  variant="outlined"
                  size="small"
                  className="agentFieldHalf"
                />
                <TextField
                  label="Max paragraphs"
                  type="number"
                  value={form.maxParagraphs || 3}
                  onChange={(event) => this.setField("maxParagraphs", event.target.value)}
                  variant="outlined"
                  size="small"
                  className="agentFieldHalf"
                />
              </div>
              <TextField
                label="Signature (kept verbatim)"
                value={form.signature || ""}
                onChange={(event) => this.setField("signature", event.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rows={2}
                className="agentField"
                placeholder={"Bob Smith\nAcme Inc"}
              />
              <div className="agentToggleRow">
                <input
                  type="checkbox"
                  id="signatureIncluded"
                  checked={Boolean(form.signatureIncluded)}
                  onChange={(event) => this.setField("signatureIncluded", event.target.checked)}
                />
                <label htmlFor="signatureIncluded">Append the signature automatically</label>
              </div>
            </div>
          </div>

          <div className="agentCard">
            <div className="agentCardTitle">Approved resources and links</div>
            <div className="agentCardHint">
              The agent may only reference these links. Any other URL is stripped before review.
            </div>
            {(form.resourceLinks || []).map((resource: any, index: number) => (
              <div className="agentResourceRow" key={resource.id || index}>
                <TextField
                  label="Name"
                  value={resource.name}
                  onChange={(event) => this.updateResource(index, "name", event.target.value)}
                  variant="outlined"
                  size="small"
                  className="agentResourceName"
                />
                <TextField
                  label="URL"
                  value={resource.url}
                  onChange={(event) => this.updateResource(index, "url", event.target.value)}
                  variant="outlined"
                  size="small"
                  className="agentResourceUrl"
                />
                <TextField
                  label="Note"
                  value={resource.note}
                  onChange={(event) => this.updateResource(index, "note", event.target.value)}
                  variant="outlined"
                  size="small"
                  className="agentResourceNote"
                />
                <Tooltip title={resource.enabled ? "Enabled" : "Disabled"}>
                  <input
                    type="checkbox"
                    checked={resource.enabled !== false}
                    onChange={(event) =>
                      this.updateResource(index, "enabled", event.target.checked)
                    }
                  />
                </Tooltip>
                <Button
                  size="small"
                  color="secondary"
                  onClick={() => this.removeResource(index)}
                >
                  <DeleteIcon fontSize="small" />
                </Button>
              </div>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={this.addResource}>
              Add resource
            </Button>
          </div>

          <div className="agentCard">
            <div className="agentCardTitle">Teach by example</div>
            <div className="agentCardHint">
              When the agent gets something wrong, add the real situation and the reply you would
              have sent. Newest examples shape the voice first.
            </div>
            <div className="agentExampleRow">
              <TextField
                label="Prospect situation or message"
                value={newExample.situation}
                onChange={(event) =>
                  this.setState({ newExample: { ...newExample, situation: event.target.value } })
                }
                variant="outlined"
                size="small"
                className="agentExampleSituation"
              />
              <TextField
                label="Preferred response"
                value={newExample.preferredResponse}
                onChange={(event) =>
                  this.setState({
                    newExample: { ...newExample, preferredResponse: event.target.value },
                  })
                }
                variant="outlined"
                size="small"
                className="agentExampleResponse"
              />
              <Button variant="outlined" onClick={this.addExample}>
                Add
              </Button>
            </div>
            <div className="agentExamplesList">
              {(form.approvedExamples || []).map((example: any, index: number) => (
                <div className="agentExampleItem" key={`${example.situation}-${index}`}>
                  <div className="agentExampleText">
                    <div className="agentExampleSituationText">{example.situation}</div>
                    <div className="agentExampleResponseText">{example.preferredResponse}</div>
                  </div>
                  <Button size="small" color="secondary" onClick={() => this.removeExample(index)}>
                    <DeleteIcon fontSize="small" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="agentCard">
            <div className="agentCardTitle">Test the agent</div>
            <div className="agentCardHint">
              Runs a full reply cycle against a sample message. Nothing is sent and nothing is
              saved.
            </div>
            <TextField
              label="Paste a sample prospect message"
              value={testMessage}
              onChange={(event) => this.setState({ testMessage: event.target.value })}
              variant="outlined"
              size="small"
              fullWidth
              multiline
              rows={3}
              className="agentField"
            />
            <div className="agentTestActions">
              <Button
                variant="contained"
                className="agentPrimaryButton"
                startIcon={<PlayArrowIcon />}
                onClick={this.runTest}
                disabled={testing || !testMessage.trim()}
              >
                {testing ? "Testing..." : "Run test"}
              </Button>
              {testResult && (
                <Button size="small" onClick={this.props.clearAgentTest}>
                  Clear result
                </Button>
              )}
            </div>
            {testResult && (
              <div className="agentTestResult">
                {testResult.error ? (
                  <div className="agentTestError">{testResult.error}</div>
                ) : (
                  <>
                    <div className="agentTestMeta">
                      <Chip
                        size="small"
                        label={testResult.decision}
                        color={testResult.decision === "reply" ? "primary" : "secondary"}
                      />
                      <span className="agentTestMetaText">
                        {testResult.classification} · confidence{" "}
                        {Math.round((testResult.confidence || 0) * 100)}%
                        {testResult.critic ? ` · critic ${testResult.critic.verdict}` : ""}
                      </span>
                    </div>
                    <div className="agentTestBody">
                      {testResult.body || "(no reply)"}
                    </div>
                    {testResult.reason && (
                      <div className="agentTestReason">Reason: {testResult.reason}</div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </Home>
    );
  }
}

const mapStateToProps = (state: any) => ({
  agent: selectAgent(state),
  testResult: selectAgentTestResult(state),
  loading: state?.agent?.loading,
  saving: state?.agent?.saving,
  testing: state?.agent?.testing,
});

export default connect(mapStateToProps, {
  getAgent,
  updateAgent,
  addAgentExample,
  testAgent,
  clearAgentTest,
})(AgentSettings);
